import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"]) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
};

export const AudioRecorder = ({ onTranscript, context = "", compact = false, label = "Registra nota vocale" }) => {
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => () => { clearInterval(timer.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const transcribe = async (blob, filename) => {
    setState("transcribing");
    try {
      const r = await api.transcribeAudio(blob, filename, context);
      if (!r.text) toast.info("Nessun parlato riconosciuto");
      else onTranscript(r.text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trascrizione non riuscita");
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timer.current);
        const type = rec.mimeType || "audio/webm";
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "webm" : "webm";
        const blob = new Blob(chunks.current, { type });
        if (blob.size < 1000) { setState("idle"); toast.info("Registrazione troppo breve"); return; }
        transcribe(blob, `nota_vocale.${ext}`);
      };
      recRef.current = rec;
      rec.start(250);
      setSecs(0);
      timer.current = setInterval(() => setSecs((s) => s + 1), 1000);
      setState("recording");
    } catch (e) {
      toast.error("Microfono non disponibile: consenti l'accesso al microfono nel browser");
    }
  };
  const stop = () => { recRef.current?.state !== "inactive" && recRef.current?.stop(); };

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (state === "transcribing") {
    return <span className={`inline-flex items-center gap-1.5 text-xs text-slate-500 ${compact ? "" : "px-3 py-2 rounded-lg border border-slate-200"}`} data-testid="audio-transcribing"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Trascrizione…</span>;
  }
  if (state === "recording") {
    return (
      <button type="button" onClick={stop} data-testid="audio-stop-button" className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 ${compact ? "px-2 py-1" : "px-3 py-2"}`}>
        <Square className="w-3.5 h-3.5 fill-current" /> Stop <span className="font-mono">{fmt(secs)}</span><span className="w-2 h-2 rounded-full bg-white animate-pulse" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <button type="button" onClick={start} data-testid="audio-record-button" title={label} className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 hover:border-orange-400 hover:text-orange-700 bg-white ${compact ? "px-2 py-1" : "px-3 py-2"}`}>
        <Mic className="w-3.5 h-3.5" /> {compact ? "" : label}
      </button>
      <input ref={fileRef} type="file" accept="audio/*,.mp3,.m4a,.wav,.webm,.mp4" className="hidden" data-testid="audio-upload-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) transcribe(f, f.name); e.target.value = ""; }} />
      <button type="button" onClick={() => fileRef.current.click()} title="Carica file audio" data-testid="audio-upload-button" className={`inline-flex items-center text-slate-400 hover:text-slate-700 rounded-lg border border-slate-200 bg-white ${compact ? "p-1" : "p-2"}`}><Upload className="w-3.5 h-3.5" /></button>
    </span>
  );
};
